const BASE = '/api';

export async function api<T = any>(path: string, opts: RequestInit = {}): Promise<T> {
  const hasBody = opts.body !== undefined && opts.body !== null;
  const baseHeaders: Record<string, string> = hasBody ? { 'content-type': 'application/json' } : {};
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    ...opts,
    headers: { ...baseHeaders, ...opts.headers },
  });
  if (res.status === 401) {
    window.location.href = '/login';
    throw new Error('unauthorized');
  }
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`${res.status}: ${text || res.statusText}`);
  }
  if (res.headers.get('content-type')?.includes('application/json')) {
    return res.json();
  }
  return res.text() as any;
}

export const TaskAPI = {
  list: (params?: Record<string, string>) =>
    api(`/tasks?${new URLSearchParams(params || {})}`),
  get: (id: number) => api(`/tasks/${id}`),
  create: (data: any) => api('/tasks', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: number, data: any) =>
    api(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: number) => api(`/tasks/${id}`, { method: 'DELETE' }),
  run: (id: number) => api(`/tasks/${id}/run`, { method: 'POST' }),
  enable: (id: number) => api(`/tasks/${id}/enable`, { method: 'POST' }),
  disable: (id: number) => api(`/tasks/${id}/disable`, { method: 'POST' }),
};

export const RunAPI = {
  list: (params?: Record<string, string>) =>
    api(`/runs?${new URLSearchParams(params || {})}`),
  get: (id: number) => api(`/runs/${id}`),
};

export const SystemAPI = {
  info: () => api('/system/overview'),
  services: () => api('/system/services'),
  restartService: (name: string) =>
    api(`/system/services/${name}/restart`, { method: 'POST' }),
  upgrade: () => api<{ ok: boolean; runId: number | null }>('/system/upgrade', { method: 'POST' }),
};

export const CertAPI = {
  list: () => api('/certs'),
  renew: (domain: string) => api(`/certs/${domain}/renew`, { method: 'POST' }),
};

export const DomainAPI = {
  local: () => api('/domains/local'),
  hostinger: () => api('/domains/hostinger'),
  dnsRecords: (domain: string) => api(`/domains/hostinger/${domain}/dns`),
};

export const ProjectAPI = {
  list: (params?: Record<string, string>) =>
    api(`/projects?${new URLSearchParams(params || {})}`),
  get: (slug: string) => api(`/projects/${slug}`),
  update: (slug: string, data: any) =>
    api(`/projects/${slug}`, { method: 'PATCH', body: JSON.stringify(data) }),
  importFromFile: () => api('/projects/import', { method: 'POST' }),
  delete: (slug: string) => api(`/projects/${slug}`, { method: 'DELETE' }),
};

export const DocsAPI = {
  tree: () => api('/docs/tree'),
  file: (path: string) => api(`/docs/file?path=${encodeURIComponent(path)}`),
};

export const AgentAPI = {
  list: () => api('/agents'),
  log: (server: string, lines = 200, project?: string) =>
    api(`/agents/${server}/log?lines=${lines}${project ? `&project=${encodeURIComponent(project)}` : ''}`),
  serviceLog: (lines = 500, level?: string) => api(`/agents/service/log?lines=${lines}${level ? `&level=${level}` : ''}`),
  sessions: (limit = 30) => api(`/agents/sessions?limit=${limit}`),
  session: (id: string) => api(`/agents/sessions/${id}`),
};

export const SettingsAPI = {
  get: () => api('/settings'),
  testAlert: () => api('/bot/test-alert', { method: 'POST' }),
};

export const ApiAPI = {
  list: () => api('/apis'),
  get: (slug: string) => api(`/apis/${slug}`),
  balance: (slug: string) => api(`/apis/${slug}/balance`),
  usage: (params: Record<string, string> = {}) => api(`/apis/usage?${new URLSearchParams(params)}`),
  costs: (params: Record<string, string> = {}) => api(`/apis/costs?${new URLSearchParams(params)}`),
  refreshLogs: () => api('/apis/refresh-logs', { method: 'POST' }),
};

export const AuthAPI = {
  me: () => api('/auth/me'),
  telegramLogin: (payload: any) =>
    api('/auth/telegram', { method: 'POST', body: JSON.stringify(payload) }),
  telegramWebApp: (initData: string) =>
    api('/auth/telegram-webapp', { method: 'POST', body: JSON.stringify({ initData }) }),
  logout: () => api('/auth/logout', { method: 'POST' }),
};
